import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  FaArrowLeft, FaBan, FaDownload, FaEdit, FaEnvelope, FaFilePowerpoint,
  FaList, FaPlus, FaPrint, FaSync, FaTrashAlt, FaUndo,
} from 'react-icons/fa';
import { MdNotificationsActive } from 'react-icons/md';
import { LuAlarmClock } from 'react-icons/lu';
import {
  addLeaveIhrmBooking, deleteBooking, resetBooking, rollbackBooking, sendBackToHRAdmin,
  sendBackToAccountant, finalTripToRequesterBooking, printPDFBooking, downloadAllDocumentsBooking,
  remindApprovalBooking,
} from 'redux/booking/travel/travelBookingSlice';
import { Card, Button, TwIconButton, ActionDropdown, confirmAlert } from 'components/common';
import BookingCancelConfirmation from './BookingCancelConfirmation';

const BookingDetailHeader = ({ booking }) => {
  const [showCancelForm, setShowCancelForm] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookingUrl = '/booking/travel';
  const bookingId = booking.id;

  const {
    allow_edit, allow_delete, allow_cancel, allow_reset, allow_rollback,
    allow_send_to_accountant, allow_send_to_admin, allow_add_leave_to_ihrm,
    allow_final_trip_to_requestor, allow_remind,
  } = booking?.permissions || {};

  const handleBookingList = () => navigate(`${bookingUrl}/list`);

  const confirmBookingAction = (message, action) => {
    confirmAlert({
      title: 'Confirmation',
      message,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        { label: 'Yes', className: 'confirm-btn-primary', onClick: async () => { try { await dispatch(action(bookingId)).unwrap(); } catch (error) { console.error(error); } } },
        { label: 'No', onClick: () => {} },
      ],
    });
  };

  const handleDeleteBooking = () => {
    confirmAlert({
      title: 'Confirmation',
      message: `Are you sure to delete travel booking <strong>${booking.booking_number}</strong>?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        { label: 'Yes', className: 'confirm-btn-primary', onClick: async () => { try { await dispatch(deleteBooking(booking.id)).unwrap(); handleBookingList(); } catch (error) { console.error(error); } } },
        { label: 'No', onClick: () => {} },
      ],
    });
  };

  const handlePrintBooking = () => dispatch(printPDFBooking(bookingId));

  const handleDownloadAllDocuments = async () => {
    try {
      await dispatch(downloadAllDocumentsBooking(bookingId)).unwrap();
    } catch (error) {
      toast.error(error || 'Download failed');
    }
  };

  const statusOne = booking?.status?.id === 1;
  const items = [];
  if (allow_edit) items.push({ label: 'Edit', icon: <FaEdit />, onClick: () => navigate(`${bookingUrl}/${bookingId}/edit`, { state: booking }) }, { divider: true });
  if (allow_send_to_accountant) items.push({ label: `Send ${statusOne ? '' : 'back'} to accountant`, icon: <FaEnvelope />, onClick: () => confirmBookingAction(`Are you sure to send back to Accountant for travel booking <strong>${booking.booking_number}</strong>?`, sendBackToAccountant) }, { divider: true });
  if (allow_send_to_admin) items.push({ label: `Send ${statusOne ? '' : 'back'} to HR admin`, icon: <FaEnvelope />, onClick: () => confirmBookingAction(`Are you sure to send back to HR Admin for travel booking <strong>${booking.booking_number}</strong>?`, sendBackToHRAdmin) }, { divider: true });
  if (allow_add_leave_to_ihrm) items.push({ label: 'Sync leave to iHRM', icon: <FaSync />, onClick: () => confirmBookingAction(`Are you sure to add leave to iHRM for travel booking <strong>${booking.booking_number}</strong>?`, addLeaveIhrmBooking) }, { divider: true });
  if (allow_final_trip_to_requestor) items.push({ label: 'Final trip to requester', icon: <MdNotificationsActive />, onClick: () => confirmBookingAction(`Are you sure to update travel booking <strong>${booking.booking_number}</strong> to requestor?`, finalTripToRequesterBooking) }, { divider: true });
  if (allow_rollback) items.push({ label: 'Rollback', icon: <FaUndo />, onClick: () => confirmBookingAction(`Are you sure to <strong>rollback</strong> travel booking <strong>${booking.booking_number}</strong>?`, rollbackBooking) }, { divider: true });
  if (allow_cancel) items.push({ label: 'Cancel', icon: <FaBan />, danger: true, onClick: () => setShowCancelForm(true) });
  if (allow_reset) items.push({ label: 'Reset', icon: <FaFilePowerpoint />, onClick: () => confirmBookingAction(`Are you sure to <strong>reset</strong> travel booking <strong>${booking.booking_number}</strong>?`, resetBooking) });
  if (allow_remind) items.push({ divider: true }, { label: 'Remind approval', icon: <LuAlarmClock />, onClick: () => confirmBookingAction(`Are you sure to remind travel booking <strong>${booking.booking_number}</strong> approval?`, remindApprovalBooking) });
  items.push({ divider: true }, { label: 'Download all documents', icon: <FaDownload />, onClick: handleDownloadAllDocuments });
  if (allow_delete) items.push({ divider: true }, { label: 'Delete', icon: <FaTrashAlt />, danger: true, onClick: handleDeleteBooking });

  return (
    <>
      <Card padding="none" className="tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <TwIconButton variant="secondary" icon={FaArrowLeft} onClick={() => navigate(-1)} title="Back" />

          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap tw-justify-end">
            <Button size="sm" variant="secondary" leftIcon={FaPlus} onClick={() => navigate('/booking/travel/new-travel-request')}>
              <span className="tw-hidden xl:tw-inline">New travel request</span>
            </Button>
            <Button size="sm" variant="secondary" leftIcon={FaPrint} onClick={handlePrintBooking}>
              <span className="tw-hidden xl:tw-inline">BTA Form</span>
            </Button>
            <Button size="sm" variant="secondary" leftIcon={FaList} onClick={handleBookingList}>
              <span className="tw-hidden xl:tw-inline">List</span>
            </Button>
            <ActionDropdown items={items} menuWidth={230} />
          </div>
        </div>
      </Card>

      {showCancelForm && (
        <BookingCancelConfirmation showModal={showCancelForm} setshowModal={setShowCancelForm} booking={booking} />
      )}
    </>
  );
};

BookingDetailHeader.propTypes = {
  booking: PropTypes.object,
};

export default BookingDetailHeader;
